'use client';
/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, Input, Button } from '@quadminds/mindkit';
import useRename from '@quadminds/mindy-app/hooks/useRename';
import type { FileItem } from '@quadminds/mindy-app/types/File/File.types';
import { RiLoader4Line } from '@remixicon/react';
import type React from 'react';

interface RenameDialogProps {
    readonly isOpen: boolean;
    readonly onClose: () => void;
    readonly onConfirm: (newName: string) => void;
    readonly item: FileItem | null;
    readonly isLoading: boolean;
}

export function RenameDialog({ isOpen, onClose, onConfirm, item, isLoading }: RenameDialogProps) {
    const { nameParts, error, handleSubmit, setNameParts, setError, t } = useRename({ item, onConfirm });

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle data-testid="rename-dialog-title">
                        {t(`modals.renameFile.${item?.type === 'folder' ? 'title1' : 'title2'}`)}
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <div className="flex items-center">
                                <Input
                                    id="name"
                                    value={nameParts.base}
                                    onChange={(e) => {
                                        setNameParts({ ...nameParts, base: e.target.value });
                                        setError('');
                                    }}
                                    placeholder={t('modals.renameFile.inputPlaceholder')}
                                    autoFocus
                                    data-testid="rename-dialog-input"
                                />
                                {item?.type === 'file' && <span className="ml-2 text-gray-500">{nameParts.ext}</span>}
                            </div>
                            {error && <p className="text-sm text-red-500">{error}</p>}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
                            {t('modals.common.cancel')}
                        </Button>
                        <Button type="submit" disabled={isLoading} data-testid="rename-dialog-submit">
                            {isLoading ? <RiLoader4Line className="animate-spin" /> : t('modals.common.save')}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
